import { create } from 'zustand';
import type {
  CalendarEvent,
  CalendarIntegration,
  Folder,
  FolderKind,
  Notice,
  NoticeLayer,
  Playlist,
  Schedule,
  ScheduleRecurrence,
  ScheduleDeliveryTarget,
  ScheduleTarget,
  ScheduleType,
  TimelineBlock,
} from '@cornerstone/shared';
import { api } from '../client/api';
import { useCalibrationStore } from './calibration';
import { toast, describeApiError } from '../components/toast';
import { isNoAssignedGroupsError } from '../components/GroupAssignmentNotice';

export type CreateScheduleInput = {
  name: string;
  enabled?: boolean;
  recurrence: ScheduleRecurrence;
  target: ScheduleTarget;
  scheduleTypeId?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  priority?: number;
  repeatCount?: number;
  transitionType?: 'none' | 'crossfade' | 'fade-to-black';
  transitionDurationMs?: number;
  // NBL_REQ_504 — device ids this schedule targets. null = all org devices.
  targetDeviceIds?: string[] | null;
  targets?: ScheduleDeliveryTarget[];
};

// =============================================================================
// Library store — playlists + schedules (separate from the calibration store
// so unrelated re-renders don't cascade). WS events `playlist:change` and
// `schedule:change` are forwarded here from the common ws client.
// =============================================================================

type LibraryState = {
  folders: Folder[];
  loadFolders: () => Promise<void>;
  /** ownerGroupId: 그 그룹 소유로(null = 공통). 생략하면 서버 기본(만든 사람 기준). */
  createFolder: (name: string, kind: FolderKind, ownerGroupId?: string | null) => Promise<Folder>;
  renameFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string, contents?: 'delete' | 'keep') => Promise<void>;
  playlists: Playlist[];
  schedules: Schedule[];
  scheduleTypes: ScheduleType[];
  timelineBlocks: TimelineBlock[];
  notices: Notice[];
  calendarRooms: CalendarIntegration[];
  calendarEvents: CalendarEvent[];
  calendarError: string | null;
  playlistError: string | null;
  scheduleError: string | null;
  noticesError: string | null;
  loadPlaylists: () => Promise<void>;
  loadSchedules: () => Promise<void>;
  loadNotices: () => Promise<void>;
  loadCalendarRooms: () => Promise<void>;
  loadCalendarEvents: (range: { from: string; to: string }) => Promise<void>;
  pollCalendarRoom: (id: string) => Promise<void>;
  /** Get-or-create the single "room board" Notice that aggregates the given
   *  rooms (or every enabled room when `roomIds` is null). Reuses the first
   *  existing room-board notice so the operator can only have one global
   *  board notice; mutating `roomIds` updates that single notice in place. */
  ensureRoomBoardNotice: (input: {
    roomIds: string[] | null;
    windowStartHour?: number;
    windowEndHour?: number;
    orientation?: 'horizontal' | 'vertical';
  }) => Promise<Notice>;
  createNotice: (input: {
    name: string;
    canvasWidth?: number;
    canvasHeight?: number;
    fitMode?: NonNullable<Notice['fitMode']>;
    canvasFill?: string | null;
    canvasFillOpacity?: number;
    folderId?: string | null;
    durationSec?: number;
    layers: NoticeLayer[];
    isTemplate?: boolean;
    ownerGroupId?: string | null;
  }) => Promise<Notice>;
  updateNotice: (
    id: string,
    patch: {
      name?: string;
      canvasWidth?: number;
      canvasHeight?: number;
    fitMode?: NonNullable<Notice['fitMode']>;
      canvasFill?: string | null;
      canvasFillOpacity?: number;
      folderId?: string | null;
      durationSec?: number;
      layers?: NoticeLayer[];
      /** Owner 전용 — 기본 템플릿 지정/해제(기준 조직에서 전 조직 전파). */
      isTemplate?: boolean;
    },
  ) => Promise<void>;
  deleteNotice: (id: string) => Promise<void>;
  /** Bulk-delete notices. Accepts an explicit id list OR `'chat'` filter
   *  (every notice whose name starts with `[Chat]`). Builtin presets
   *  (`preset-*`) are always skipped server-side. Returns the deleted ids
   *  so the caller can show a precise toast. */
  bulkDeleteNotices: (
    body: { ids: string[] } | { filter: 'chat' },
  ) => Promise<{ deleted: string[]; skipped: Array<{ id: string; reason: string }> }>;
  uploadNoticeThumbnail: (id: string, blob: Blob) => Promise<void>;
  receiveNoticeChange: (payload: NoticeChangePayload) => void;
  createPlaylist: (input: {
    name: string;
    ownerGroupId?: string | null;
    folderId?: string | null;
    resolutionWidth?: number;
    resolutionHeight?: number;
    imageDurationSec?: number;
    transitionType?: 'none' | 'crossfade' | 'fade-to-black';
    transitionDurationMs?: number;
    items?: Array<{
      mediaId?: string | null;
      noticeId?: string | null;
      durationSec: number | null;
    }>;
    mediaIds?: string[];
  }) => Promise<Playlist>;
  updatePlaylist: (
    id: string,
    patch: {
      name?: string;
      folderId?: string | null;
      resolutionWidth?: number;
      resolutionHeight?: number;
      imageDurationSec?: number;
      transitionType?: 'none' | 'crossfade' | 'fade-to-black';
      transitionDurationMs?: number;
      items?: Array<{
      mediaId?: string | null;
      noticeId?: string | null;
      durationSec: number | null;
    }>;
      mediaIds?: string[];
    },
  ) => Promise<void>;
  deletePlaylist: (id: string) => Promise<{ transferred: true; detail: string } | undefined>;
  applyPlaylist: (id: string) => Promise<void>;
  createSchedule: (input: CreateScheduleInput) => Promise<Schedule>;
  updateSchedule: (id: string, patch: Partial<Schedule>) => Promise<void>;
  deleteSchedule: (id: string) => Promise<void>;
  loadScheduleTypes: () => Promise<void>;
  createScheduleType: (input: { name: string; weekdays: number[]; color?: string; deviceId?: string | null; groupId?: string }) => Promise<ScheduleType>;
  updateScheduleType: (id: string, patch: Partial<ScheduleType>) => Promise<void>;
  deleteScheduleType: (id: string) => Promise<void>;
  loadTimelineBlocks: (scheduleTypeId?: string) => Promise<void>;
  createTimelineBlock: (input: {
    scheduleTypeId: string;
    playlistId: string;
    startMinute: number;
    endMinute: number;
    color?: string;
    label?: string;
  }) => Promise<TimelineBlock>;
  updateTimelineBlock: (id: string, patch: Partial<TimelineBlock>) => Promise<void>;
  deleteTimelineBlock: (id: string) => Promise<void>;
  receivePlaylistChange: (payload: PlaylistChangePayload) => void;
  receiveScheduleChange: (payload: ScheduleChangePayload) => void;
  receiveScheduleTypeChange: (payload: ScheduleTypeChangePayload) => void;
  receiveTimelineBlockChange: (payload: TimelineBlockChangePayload) => void;
};

export type ScheduleTypeChangePayload =
  | { kind: 'created'; item: ScheduleType }
  | { kind: 'updated'; item: ScheduleType }
  | { kind: 'deleted'; id: string };

export type TimelineBlockChangePayload =
  | { kind: 'created'; item: TimelineBlock }
  | { kind: 'updated'; item: TimelineBlock }
  | { kind: 'deleted'; id: string };

export type PlaylistChangePayload =
  | { kind: 'created'; item: Playlist }
  | { kind: 'updated'; item: Playlist }
  | { kind: 'deleted'; id: string };

export type ScheduleChangePayload =
  | { kind: 'created'; item: Schedule }
  | { kind: 'updated'; item: Schedule }
  | { kind: 'deleted'; id: string };

export type NoticeChangePayload =
  | { kind: 'created'; item: Notice }
  | { kind: 'updated'; item: Notice }
  | { kind: 'deleted'; id: string }
  /** 기본 템플릿 지정/해제 전파 — 조직별 사본이 바뀌었으니 목록을 다시 받는다. */
  | { kind: 'seed'; id: string };

export const useLibraryStore = create<LibraryState>((set, get) => ({
  folders: [],
  loadFolders: async () => {
    try {
      const folders = await api.listFolders();
      set({ folders });
    } catch {
      /* non-fatal */
    }
  },
  createFolder: async (name, kind, ownerGroupId) => {
    const folder = await api.createFolder(name, kind, ownerGroupId);
    set((s) => ({ folders: [...s.folders, folder] }));
    return folder;
  },
  renameFolder: async (id, name) => {
    const folder = await api.renameFolder(id, name);
    set((s) => ({ folders: s.folders.map((f) => (f.id === id ? folder : f)) }));
  },
  deleteFolder: async (id, contents = 'delete') => {
    await api.deleteFolder(id, contents);
    set((s) => ({ folders: s.folders.filter((f) => f.id !== id) }));
  },
  playlists: [],
  schedules: [],
  scheduleTypes: [],
  timelineBlocks: [],
  notices: [],
  calendarRooms: [],
  calendarEvents: [],
  calendarError: null,
  playlistError: null,
  scheduleError: null,
  noticesError: null,

  loadPlaylists: async () => {
    try {
      const playlists = await api.listPlaylists();
      set({ playlists, playlistError: null });
    } catch (e) {
      // 담당 그룹이 아직 없는 계정은 '실패'가 아니라 '볼 것이 없는' 상태다.
      // 빨간 배너 대신 화면이 빈 상태 안내를 그린다(2026-09-17 대교).
      if (isNoAssignedGroupsError(e)) { set({ playlists: [], playlistError: null }); return; }
      set({ playlistError: describeApiError(e) });
    }
  },
  loadSchedules: async () => {
    try {
      const schedules = await api.listSchedules();
      set({ schedules, scheduleError: null });
    } catch (e) {
      if (isNoAssignedGroupsError(e)) { set({ schedules: [], scheduleError: null }); return; }
      set({ scheduleError: describeApiError(e) });
    }
  },

  createPlaylist: async (input) => {
    const item = await api.createPlaylist(input);
    // De-dupe in case the WS `playlist:change` event arrived first.
    set((s) => {
      if (s.playlists.some((p) => p.id === item.id)) return {};
      return { playlists: [item, ...s.playlists] };
    });
    return item;
  },
  updatePlaylist: async (id, patch) => {
    // 화면이 마지막으로 읽은 revision을 함께 보낸다. 다른 관리자가 먼저 저장했으면
    // 서버가 409로 막아, 조용히 덮어쓰고 양쪽 다 '저장되었습니다'를 보는 일을 없앤다(R12).
    const known = get().playlists.find((p) => p.id === id);
    const item = await api.updatePlaylist(id, {
      ...patch,
      ...(typeof known?.revision === 'number' ? { expectedRevision: known.revision } : {}),
    });
    // Cross-group saves can replace source IDs with newly created copies.
    // Load their metadata before publishing the saved playlist to the editor.
    if (item.items.some((entry) =>
      entry.noticeId && !get().notices.some((notice) => notice.id === entry.noticeId)
      || entry.mediaId && !useCalibrationStore.getState().mediaList.some((media) => media.id === entry.mediaId))) {
      await Promise.all([get().loadNotices(), useCalibrationStore.getState().loadMedia()]);
    }
    set((s) => ({ playlists: s.playlists.map((p) => (p.id === id ? item : p)) }));
  },
  deletePlaylist: async (id) => {
    const result = await api.deletePlaylist(id);
    set((s) => ({ playlists: s.playlists.filter((p) => p.id !== id) }));
    return result;
  },
  applyPlaylist: async (id) => {
    await api.applyPlaylist(id);
  },

  createSchedule: async (input) => {
    const item = await api.createSchedule(input);
    set((s) => {
      if (s.schedules.some((p) => p.id === item.id)) return {};
      return { schedules: [item, ...s.schedules] };
    });
    return item;
  },
  updateSchedule: async (id, patch) => {
    const item = await api.updateSchedule(id, patch);
    set((s) => ({ schedules: s.schedules.map((p) => (p.id === id ? item : p)) }));
  },
  deleteSchedule: async (id) => {
    const pending = await api.deleteSchedule(id);
    set((s) => ({ schedules: pending ? s.schedules.map(p => p.id === id ? pending : p) : s.schedules.filter(p => p.id !== id) }));
    if (pending) toast('삭제 요청이 승인 대기 중입니다. 현재 편성은 유지됩니다.', 'info');
  },

  loadScheduleTypes: async () => {
    try {
      const scheduleTypes = await api.listScheduleTypes();
      set({ scheduleTypes });
    } catch (e) {
      set({ scheduleError: describeApiError(e) });
    }
  },
  createScheduleType: async (input) => {
    const item = await api.createScheduleType(input);
    set((s) => {
      if (s.scheduleTypes.some((t) => t.id === item.id)) return {};
      return { scheduleTypes: [...s.scheduleTypes, item] };
    });
    return item;
  },
  updateScheduleType: async (id, patch) => {
    const item = await api.updateScheduleType(id, patch);
    set((s) => ({
      scheduleTypes: s.scheduleTypes.map((t) => (t.id === id ? item : t)),
    }));
  },
  deleteScheduleType: async (id) => {
    const pending = await api.deleteScheduleType(id);
    set((s) => ({
      scheduleTypes: pending ? s.scheduleTypes.map(t => t.id === id ? pending : t) : s.scheduleTypes.filter(t => t.id !== id),
      timelineBlocks: pending ? s.timelineBlocks : s.timelineBlocks.filter(b => b.scheduleTypeId !== id),
    }));
    if (pending) toast('삭제 요청이 승인 대기 중입니다. 현재 타임라인은 유지됩니다.', 'info');
  },
  loadTimelineBlocks: async (scheduleTypeId) => {
    try {
      const blocks = await api.listTimelineBlocks(scheduleTypeId);
      // Replace blocks for the given type, or all if no filter
      set((s) => {
        if (!scheduleTypeId) return { timelineBlocks: blocks };
        const others = s.timelineBlocks.filter((b) => b.scheduleTypeId !== scheduleTypeId);
        return { timelineBlocks: [...others, ...blocks] };
      });
    } catch (e) {
      set({ scheduleError: describeApiError(e) });
    }
  },
  createTimelineBlock: async (input) => {
    // 생성은 모달에서만 호출되고 모달이 인라인 에러를 보여주므로(중복 방지) 여기선
    // 토스트하지 않는다.
    const item = await api.createTimelineBlock(input);
    set((s) => {
      if (s.timelineBlocks.some((b) => b.id === item.id)) return {};
      return { timelineBlocks: [...s.timelineBlocks, item] };
    });
    return item;
  },
  updateTimelineBlock: async (id, patch) => {
    // 드래그/리사이즈 등 fire-and-forget 경로가 있어, 실패를 여기서 토스트로 알린다.
    let item;
    try {
      item = await api.updateTimelineBlock(id, patch);
    } catch (e) {
      toast(describeApiError(e), 'error');
      throw e;
    }
    set((s) => ({
      timelineBlocks: s.timelineBlocks.map((b) => (b.id === id ? item : b)),
    }));
  },
  deleteTimelineBlock: async (id) => {
    let pending: TimelineBlock | null;
    try {
      pending = await api.deleteTimelineBlock(id);
    } catch (e) {
      toast(describeApiError(e), 'error');
      throw e;
    }
    set((s) => ({
      timelineBlocks: pending ? s.timelineBlocks.map(b => b.id === id ? pending : b) : s.timelineBlocks.filter(b => b.id !== id),
    }));
    if (pending) toast('삭제 요청이 승인 대기 중입니다. 현재 블록은 유지됩니다.', 'info');
  },

  receivePlaylistChange: (payload) =>
    set((s) => {
      if (payload.kind === 'deleted') {
        return { playlists: s.playlists.filter((p) => p.id !== payload.id) };
      }
      if (payload.kind === 'created') {
        if (s.playlists.some((p) => p.id === payload.item.id)) return {};
        return { playlists: [payload.item, ...s.playlists] };
      }
      return {
        playlists: s.playlists.map((p) => (p.id === payload.item.id ? payload.item : p)),
      };
    }),
  receiveScheduleChange: (payload) =>
    set((s) => {
      if (payload.kind === 'deleted') {
        return { schedules: s.schedules.filter((p) => p.id !== payload.id) };
      }
      if (payload.kind === 'created') {
        if (s.schedules.some((p) => p.id === payload.item.id)) return {};
        return { schedules: [payload.item, ...s.schedules] };
      }
      return {
        schedules: s.schedules.map((p) => (p.id === payload.item.id ? payload.item : p)),
      };
    }),

  // -------- Notices --------
  loadNotices: async () => {
    try {
      const notices = await api.listNotices();
      set({ notices, noticesError: null });
    } catch (e) {
      if (isNoAssignedGroupsError(e)) { set({ notices: [], noticesError: null }); return; }
      set({ noticesError: describeApiError(e) });
    }
  },
  createNotice: async (input) => {
    const item = await api.createNotice(input);
    set((s) => {
      if (s.notices.some((n) => n.id === item.id)) return {};
      return { notices: [item, ...s.notices] };
    });
    return item;
  },
  updateNotice: async (id, patch) => {
    const known = get().notices.find((n) => n.id === id);
    const item = await api.updateNotice(id, {
      ...patch,
      ...(typeof known?.revision === 'number' ? { expectedRevision: known.revision } : {}),
    });
    set((s) => ({
      notices: s.notices.map((n) => (n.id === id ? item : n)),
    }));
  },
  deleteNotice: async (id) => {
    await api.deleteNotice(id);
    set((s) => ({ notices: s.notices.filter((n) => n.id !== id) }));
  },
  bulkDeleteNotices: async (body) => {
    const result = await api.bulkDeleteNotices(body);
    if (result.deleted.length > 0) {
      const deletedSet = new Set(result.deleted);
      set((s) => ({
        notices: s.notices.filter((n) => !deletedSet.has(n.id)),
      }));
    }
    return result;
  },
  uploadNoticeThumbnail: async (id, blob) => {
    const item = await api.uploadNoticeThumbnail(id, blob);
    set((s) => ({
      notices: s.notices.map((n) => (n.id === id ? item : n)),
    }));
  },

  // -------- Calendar (Phase 6.6 — Rooms tab) --------
  loadCalendarRooms: async () => {
    try {
      const calendarRooms = await api.listCalendarIntegrations();
      set({ calendarRooms, calendarError: null });
    } catch (e) {
      set({ calendarError: (e as Error).message });
    }
  },
  loadCalendarEvents: async (range) => {
    try {
      const calendarEvents = await api.listCalendarEvents(range);
      set({ calendarEvents, calendarError: null });
    } catch (e) {
      set({ calendarError: (e as Error).message });
    }
  },
  pollCalendarRoom: async (id) => {
    try {
      const updated = await api.pollCalendarRoomNow(id);
      set((s) => ({
        calendarRooms: s.calendarRooms.map((r) => (r.id === id ? updated : r)),
      }));
    } catch (e) {
      set({ calendarError: (e as Error).message });
    }
  },
  ensureRoomBoardNotice: async ({
    roomIds,
    windowStartHour = 8,
    windowEndHour = 20,
    orientation = 'vertical',
  }) => {
    const layer = {
      id: 'room-board',
      kind: 'roomStatus' as const,
      // Half-canvas footprint (left wall only) so the corner-projection warp
      // doesn't slice the board in two. Operators can resize via the Notice
      // editor when running on a flat / bypass-warp output.
      x: 0,
      y: 0,
      width: 960,
      height: 1080,
      z: 0,
      opacity: 1,
      rotation: 0,
      roomIntegrationIds: roomIds,
      windowStartHour,
      windowEndHour,
      orientation,
    };

    // Reuse the single board notice (any notice carrying a roomStatus layer).
    const existing = get().notices.find((n) =>
      n.layers.some((l) => l.kind === 'roomStatus'),
    );
    if (existing) {
      const updated = await api.updateNotice(existing.id, { layers: [layer] });
      set((s) => ({
        notices: s.notices.map((n) => (n.id === updated.id ? updated : n)),
      }));
      return updated;
    }

    const created = await api.createNotice({
      name: '회의실 상태',
      canvasWidth: 1920,
      canvasHeight: 1080,
      layers: [layer],
    });
    set((s) => {
      if (s.notices.some((n) => n.id === created.id)) return {};
      return { notices: [created, ...s.notices] };
    });
    return created;
  },
  receiveNoticeChange: (payload) => {
    if (payload.kind === 'seed') {
      void get().loadNotices();
      return;
    }
    set((s) => {
      if (payload.kind === 'deleted') {
        return { notices: s.notices.filter((n) => n.id !== payload.id) };
      }
      if (payload.kind === 'created') {
        if (s.notices.some((n) => n.id === payload.item.id)) return {};
        return { notices: [payload.item, ...s.notices] };
      }
      return {
        notices: s.notices.map((n) =>
          n.id === payload.item.id ? payload.item : n,
        ),
      };
    });
  },
  receiveScheduleTypeChange: (payload) =>
    set((s) => {
      if (payload.kind === 'deleted') {
        return {
          scheduleTypes: s.scheduleTypes.filter((t) => t.id !== payload.id),
          timelineBlocks: s.timelineBlocks.filter((b) => b.scheduleTypeId !== payload.id),
        };
      }
      if (payload.kind === 'created') {
        if (s.scheduleTypes.some((t) => t.id === payload.item.id)) return {};
        return { scheduleTypes: [...s.scheduleTypes, payload.item] };
      }
      return {
        scheduleTypes: s.scheduleTypes.map((t) => (t.id === payload.item.id ? payload.item : t)),
      };
    }),
  receiveTimelineBlockChange: (payload) =>
    set((s) => {
      if (payload.kind === 'deleted') {
        return { timelineBlocks: s.timelineBlocks.filter((b) => b.id !== payload.id) };
      }
      if (payload.kind === 'created') {
        if (s.timelineBlocks.some((b) => b.id === payload.item.id)) return {};
        return { timelineBlocks: [...s.timelineBlocks, payload.item] };
      }
      return {
        timelineBlocks: s.timelineBlocks.map((b) => (b.id === payload.item.id ? payload.item : b)),
      };
    }),
}));
